import { useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useFecharAoClicarFora } from './fechar-fora'
import { IconeSetaBaixo } from './icones'
import { CLASSE_DO_ITEM_ATIVO, CLASSE_DO_ITEM_INATIVO } from './navegacao-estilo'
import type { ItemDeNavegacao } from './NavegacaoPrincipal'
import { juntar } from './ui'

/**
 * "Mais": as telas da barra que não couberam na largura da janela, da última
 * para a primeira. Estando numa delas, o botão troca "Mais" pelo nome da tela
 * e ganha a marca de item ativo, para a barra não ficar sem nada marcado.
 *
 * Teve também páginas próprias, que não tinham lugar fixo na barra: a última
 * foi Anúncios, que mudou para o Marketing (ADR 0068).
 */
export function MenuMaisPaginas({
  extras,
  compacto = false,
}: {
  extras: ItemDeNavegacao[]
  /** Sem espaço para o nome da tela aberta (celular): só o ícone, e o nome no `title`. */
  compacto?: boolean
}) {
  const [aberto, setAberto] = useState(false)
  /*
    Onde a lista abre, decidido na hora de abrir.

    Alinhada à esquerda do botão, que é o natural, ela passava da borda da
    janela sempre que o botão ficava perto da direita. Alinhar pela direita
    resolvia no computador, mas no celular, com o botão no meio de uma tela de
    375px, a lista de 288px saía 58px pela esquerda. Agora ela começa no botão e
    recua só o que precisar para caber, com 8px de folga de cada lado.
  */
  const [posicao, setPosicao] = useState({ deslocamento: 0, largura: 288 })
  const botao = useRef<HTMLButtonElement>(null)
  const local = useLocation()
  const caixa = useFecharAoClicarFora<HTMLDivElement>(aberto, () => setAberto(false))

  if (extras.length === 0) return null

  const atual = extras.find((p) => local.pathname.startsWith(p.para))

  return (
    <div ref={caixa} className="relative shrink-0">
      <button
        type="button"
        ref={botao}
        onClick={() => {
          const caixaDoBotao = botao.current?.getBoundingClientRect()
          if (caixaDoBotao) {
            const largura = Math.min(288, window.innerWidth - 16)
            const inicio = Math.min(
              Math.max(caixaDoBotao.left, 8),
              window.innerWidth - 8 - largura,
            )
            setPosicao({ deslocamento: inicio - caixaDoBotao.left, largura })
          }
          setAberto((a) => !a)
        }}
        aria-expanded={aberto}
        aria-haspopup="menu"
        title={compacto && atual ? `${atual.rotulo}: outras telas` : undefined}
        className={juntar(
          'relative flex items-center gap-1.5 rounded-full py-1.5 text-sm transition',
          compacto && atual ? 'px-2.5' : 'px-3',
          atual ? CLASSE_DO_ITEM_ATIVO : CLASSE_DO_ITEM_INATIVO,
          aberto && !atual && 'bg-white/8 text-tinta-900',
        )}
      >
        {atual ? <atual.Icone className="h-4 w-4 shrink-0" /> : null}
        {atual ? (
          <span className={compacto ? 'sr-only' : undefined}>{atual.rotulo}</span>
        ) : (
          'Mais'
        )}
        <IconeSetaBaixo
          className={juntar('h-3.5 w-3.5 shrink-0 transition-transform', aberto && 'rotate-180')}
        />
      </button>

      {aberto ? (
        <div
          role="menu"
          className="absolute top-full z-30 mt-2 rounded-2xl bg-superficie p-1.5 shadow-alta ring-1 ring-tinta-200"
          style={{ left: posicao.deslocamento, width: posicao.largura }}
        >
          {extras.map((item) => (
            <NavLink
              key={item.para}
              to={item.para}
              role="menuitem"
              onClick={() => setAberto(false)}
              className={({ isActive }) =>
                juntar(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition',
                  isActive
                    ? 'bg-marca/5 font-semibold text-marca'
                    : 'font-medium text-tinta-900 hover:bg-tinta-50',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.Icone
                    className={juntar(
                      'h-4 w-4 shrink-0',
                      isActive ? 'text-marca' : 'text-tinta-500',
                    )}
                  />
                  {item.rotulo}
                </>
              )}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  )
}
